import React, { useState, useEffect } from 'react';
import { enableWebPush, disableWebPush, webPushMaybeSupported } from '../firebase.js';
import { isIOS, isStandalone } from '../util/platform.js';

import { appStoreUrl } from '../util/appStore.js';
// Campaign-tokened store URL (funnel audit R10) — per-surface ct.
const APP_STORE_URL = appStoreUrl('web_push_optin');

// WP4-C — "Turn on reminders" opt-in + off-switch. Shown in the locked Play
// card to a SECURED user (the callables reject anonymous accounts). One tap →
// browser permission → FCM token registered server-side, enrolling them in the
// morning (9am) + evening-results (6pm) pushes. The "on" state offers a "Turn
// off" link that clears the server token (browser permission can't be revoked
// from JS — we mirror "off" with a local flag, see disableWebPush).
// `showHint` (default true) renders the descriptive "morning nudge…" line under
// the button. Profile passes false to keep that surface tidy (the button is
// self-explanatory there); the denied/error feedback still shows regardless.
export default function WebPushOptIn({ showHint = true }) {
  // "On" = browser granted AND we haven't locally turned it off. The local
  // flag matters because turning off can't revoke the browser permission, so
  // permission alone would wrongly read as "on" after the user opted out.
  const localOff =
    typeof localStorage !== 'undefined' && localStorage.getItem('ps_web_push_off') === '1';
  const alreadyOn =
    typeof Notification !== 'undefined' &&
    Notification.permission === 'granted' &&
    !localOff;
  const [state, setState] = useState(alreadyOn ? 'on' : 'idle');
  // idle | working | on | offing | denied | error
  // True when a "turn off" attempt failed — the UI flips back to "on" (the
  // token is still live), so surface why instead of silently reverting (X4).
  const [offError, setOffError] = useState(false);

  // Permission can be granted/revoked in another tab or in browser settings
  // while this view is mounted; the once-at-mount read above would then go
  // stale. Re-derive on focus / visibility so the button reflects reality
  // without a full reload (audit B8). Never interrupts an in-flight action.
  useEffect(() => {
    const sync = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (typeof Notification === 'undefined') return;
      const off =
        typeof localStorage !== 'undefined' &&
        localStorage.getItem('ps_web_push_off') === '1';
      const on = Notification.permission === 'granted' && !off;
      setState((s) => {
        if (s === 'working' || s === 'offing') return s; // don't clobber an action
        if (on && s !== 'on') return 'on';
        if (!on && s === 'on') return 'idle';
        return s;
      });
    };
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', sync);
    return () => {
      window.removeEventListener('focus', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  // Browser can't do push. On iPHONE SAFARI that's not "no path to
  // reminders" — it's the app's biggest web audience hitting an invisible
  // wall: iOS only exposes PushManager to Home-Screen-installed PWAs, so
  // this component used to silently render NOTHING for every iPhone
  // browser tab (Tom 2026-07-05: "maybe we need better instructions").
  // Instead of vanishing, point them at the path that actually delivers
  // notifications on iPhone — the native app (which also banks the +180
  // download bonus). A user who installed the PWA to their Home Screen
  // (iOS 16.4+) has PushManager and never reaches this branch. Other
  // genuinely unsupported browsers still render nothing.
  if (!webPushMaybeSupported()) {
    if (isIOS() && !isStandalone()) {
      return (
        <div style={{ margin: '0 0 14px' }}>
          <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer"
             style={{ ...btn, display: 'block', textAlign: 'center', textDecoration: 'none' }}>
            <BellIcon /> Get reminders with the iPhone app
          </a>
          {showHint && (
            <p className="ps-muted ps-center" style={{ ...line, fontSize: 12 }}>
              Safari can’t send reminders from a browser tab — the free app pings
              you when the new six drop and when your results land (up to 180 coins).
            </p>
          )}
        </div>
      );
    }
    return null;
  }

  const turnOff = async () => {
    setState('offing');
    setOffError(false);
    const res = await disableWebPush();
    if (res.ok) {
      setState('idle');
    } else {
      setState('on'); // token still live — stay "on" and explain (X4)
      setOffError(true);
    }
  };

  if (state === 'on') {
    return (
      <div style={line}>
        <p className="ps-center" style={{ margin: 0, color: 'var(--gold)', fontWeight: 700 }}>
          <BellIcon /> Reminders on — we’ll ping you when results drop.{' '}
          <button onClick={turnOff} style={offLink}>Turn off</button>
        </p>
        {offError && (
          <p
            role="alert"
            className="ps-center"
            style={{ margin: '4px 0 0', color: 'var(--danger)', fontWeight: 600, fontSize: 12 }}
          >
            Couldn’t turn reminders off just now — try again.
          </p>
        )}
      </div>
    );
  }
  if (state === 'offing') {
    return (
      <p className="ps-muted ps-center" style={{ ...line, fontWeight: 700 }}>
        Turning off…
      </p>
    );
  }

  const onClick = async () => {
    setState('working');
    const res = await enableWebPush();
    setState(res.ok ? 'on' : res.reason === 'denied' ? 'denied' : 'error');
  };

  // Denied/error feedback always shows; the descriptive hint is suppressed when
  // showHint is false (Profile).
  const subtext =
    state === 'denied'
      ? 'Notifications are blocked — turn them on in your browser settings.'
      : state === 'error'
        ? 'Couldn’t turn that on just now. Try again in a moment.'
        : showHint
          ? 'A morning nudge for the new six, and a ping when your results drop.'
          : null;
  return (
    <div style={{ margin: '0 0 14px' }}>
      <button onClick={onClick} disabled={state === 'working'} style={btn}>
        {state === 'working' ? 'Turning on…' : <><BellIcon /> Turn on reminders</>}
      </button>
      {subtext && (
        <p className="ps-muted ps-center" style={{ ...line, fontSize: 12 }}>
          {subtext}
        </p>
      )}
    </div>
  );
}

// Bell icon (currentColor → inherits the gold text color) — replaces the 🔔
// emoji, which rendered inconsistently across platforms.
function BellIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      style={{ verticalAlign: '-3px', marginRight: 6, flexShrink: 0 }}
    >
      <path d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22zM19 17l-1.6-1.9V10a5.4 5.4 0 0 0-4.4-5.31V4a1 1 0 0 0-2 0v.69A5.4 5.4 0 0 0 6.6 10v5.1L5 17a.9.9 0 0 0 .7 1.5h12.6A.9.9 0 0 0 19 17z" />
    </svg>
  );
}

const btn = {
  width: '100%',
  padding: '13px 0',
  borderRadius: 'var(--radius-sm)',
  border: '1.5px solid var(--gold)',
  background: 'transparent',
  color: 'var(--gold)',
  fontWeight: 800,
  fontSize: 15,
};
const line = { margin: '6px 0 0', lineHeight: 1.4 };
// Inline "Turn off" — a quiet text link so it doesn't compete with the
// gold "reminders on" confirmation, but is discoverable next to it.
const offLink = {
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  color: 'var(--secondary-text)',
  fontWeight: 600,
  fontSize: 12,
  textDecoration: 'underline',
};
